import React, { useState } from "react";
import { Checkbox, Drawer, Space } from "antd";
import { isEmpty } from "lodash";
import ColumnVisibilityPanel from "./ColumnVisibilityPanel";
import CustomButton from "../commonComponents/button";
import DebounceInput from "../commonComponents/DebounceInput";

const ColumnVisibilityDrawer = ({
  isVisible,
  onClose,
  columns,
  visibleColumns,
  defaultVisibleColumns,
  onColumnVisibilityChange,
  onApplyChanges,
  onResetDefaults,
  // Drawer heading — overridable (e.g. media report groups metrics + columns).
  title = "Show/Hide Columns",
  // Optional extra grouped section rendered ABOVE the columns list. When
  // provided, the columns list gets a "Data Columns" heading so the two groups
  // read as distinct, and a SINGLE top search filters BOTH the overview items
  // and the columns. Omitted → drawer behaves exactly as before (the columns
  // panel renders its own internal search).
  // Shape: { title, items: [{ key, title }], selectedKeys: string[],
  //          onToggle: (key, checked) => void, columnsTitle?: string }
  overviewSection = null,
}) => {
  const grouped = !isEmpty(overviewSection);

  // Grouped-mode search spans both sections; only used when grouped.
  const [search, setSearch] = useState("");
  const term = search.toLowerCase();

  const overviewItems = grouped
    ? overviewSection.items.filter(item => item.title?.toLowerCase().includes(term))
    : [];

  return (
    <Drawer
      title={title}
      open={isVisible}
      onClose={onClose}
      width={400}
      footer={
        <div className="flex justify-between items-center">
          <Space>
            <CustomButton btnText="Apply" type="primary" handleClick={onApplyChanges} />
            <CustomButton btnText="Cancel" handleClick={onClose} />
          </Space>
          <CustomButton btnText="Reset to default" type="link" handleClick={onResetDefaults} />
        </div>
      }
    >
      {grouped && (
        <DebounceInput
          allowClear
          className="w-full mb-4"
          placeholder="Search"
          value={search}
          onChange={setSearch}
        />
      )}

      {grouped && overviewItems.length > 0 && (
        <div className="mb-6">
          <div className="text-black-88 text-sm font-semibold mb-3">{overviewSection.title}</div>
          <div className="flex flex-col gap-2">
            {overviewItems.map(item => (
              <Checkbox
                key={item.key}
                checked={overviewSection.selectedKeys.includes(item.key)}
                onChange={e => overviewSection.onToggle(item.key, e.target.checked)}
              >
                {item.title}
              </Checkbox>
            ))}
          </div>
        </div>
      )}

      {grouped && (
        <div className="text-black-88 text-sm font-semibold mb-3">
          {overviewSection.columnsTitle || "Data Columns"}
        </div>
      )}

      <ColumnVisibilityPanel
        columns={columns}
        visibleColumns={visibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        onColumnVisibilityChange={onColumnVisibilityChange}
        // In grouped mode the drawer owns the search; hide the panel's own and
        // drive its filtering from the shared term.
        hideSearch={grouped}
        externalSearch={search}
      />
    </Drawer>
  );
};

export default ColumnVisibilityDrawer;
